/**
 * ProductOptionsDialog - POS Selection Dialog for Variants, OHNE, and Extras
 * 
 * 3-Click Flow:
 * 1. Select variant (if has_variants)
 * 2. Toggle "OHNE" options (if has_removable_ingredients)
 * 3. Add extras (if has_extras)
 * 
 * Design principles:
 * - "OHNE" options always appear ABOVE extras
 * - Single tap toggles "OHNE" (no confirmation needed)
 * - Visual hierarchy: OHNE is red/prominent, Extras are green/secondary
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Check, X, Plus, Minus, Ban, Sparkles } from 'lucide-react';

export default function ProductOptionsDialog({
  product,
  isOpen,
  onClose,
  onConfirm
}) {
  // Selected variant
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Removed ingredients (OHNE)
  const [removedIngredients, setRemovedIngredients] = useState([]);
  
  // Selected extras
  const [selectedExtras, setSelectedExtras] = useState([]);
  
  // Calculate total price
  const [totalPrice, setTotalPrice] = useState(0);

  // Initialize state when product changes
  useEffect(() => {
    if (product && isOpen) {
      // Set default variant
      if (product.has_variants && product.variants?.length > 0) {
        const defaultVariant = product.variants.find(v => v.is_default) || product.variants[0];
        setSelectedVariant(defaultVariant);
      } else {
        setSelectedVariant(null);
      }
      
      setRemovedIngredients([]);
      setSelectedExtras([]);
    }
  }, [product, isOpen]);

  // Calculate total price
  useEffect(() => {
    if (!product) return;
    
    let price = selectedVariant?.price || product.price || product.base_price || 0;
    
    // Add extras prices
    const extrasTotal = selectedExtras.reduce((sum, extra) => sum + (extra.price || 0), 0);
    
    setTotalPrice(price + extrasTotal);
  }, [product, selectedVariant, selectedExtras]);

  // Toggle ingredient removal
  const toggleIngredient = (ingredient) => {
    setRemovedIngredients(prev => {
      const exists = prev.find(i => i.ingredient_id === ingredient.ingredient_id);
      if (exists) {
        return prev.filter(i => i.ingredient_id !== ingredient.ingredient_id);
      }
      return [...prev, ingredient];
    });
  };

  // Toggle extra selection
  const toggleExtra = (extra) => {
    setSelectedExtras(prev => {
      const exists = prev.find(e => e.extra_id === extra.extra_id);
      if (exists) {
        return prev.filter(e => e.extra_id !== extra.extra_id);
      }
      return [...prev, extra];
    });
  };

  // Handle confirm
  const handleConfirm = () => {
    onConfirm?.({
      product,
      selectedVariant,
      removedIngredients,
      selectedExtras,
      totalPrice,
      // Generate display text for order
      modifiersText: generateModifiersText()
    });
    onClose();
  };

  // Generate modifiers text for display
  const generateModifiersText = () => {
    const parts = [];
    
    // OHNE always first
    removedIngredients.forEach(ing => {
      parts.push(`OHNE ${ing.name}`);
    });
    
    // Then extras
    selectedExtras.forEach(extra => {
      parts.push(`+ ${extra.name}`);
    });
    
    return parts.join(', ');
  };

  // Get removable ingredients only
  const removableIngredients = product?.ingredients?.filter(i => i.is_removable) || [];
  
  // Get all extras from all groups
  const allExtras = product?.extra_groups?.flatMap(g => g.extras) || [];

  if (!product) return null;

  // Check if product has any options
  const hasOptions = product.has_variants || 
                     (product.has_removable_ingredients && removableIngredients.length > 0) ||
                     (product.has_extras && allExtras.length > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="product-options-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{product.name}</span>
            <Badge variant="outline" className="text-lg font-bold" data-testid="total-price">
              €{totalPrice.toFixed(2)}
            </Badge>
          </DialogTitle>
          {product.description && (
            <p className="text-sm text-muted-foreground">{product.description}</p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* === VARIANT SELECTION === */}
          {product.has_variants && product.variants?.length > 0 && (
            <div>
              <h4 className="font-medium mb-2 text-sm text-muted-foreground">Größe wählen</h4>
              <div className="grid grid-cols-2 gap-2">
                {product.variants.map(variant => (
                  <Button
                    key={variant.variant_id}
                    variant={selectedVariant?.variant_id === variant.variant_id ? "default" : "outline"}
                    className="h-auto py-3 flex flex-col items-center justify-center"
                    onClick={() => setSelectedVariant(variant)}
                    data-testid={`variant-${variant.short_name || variant.name}`}
                  >
                    <span className="font-medium">{variant.name}</span>
                    <span className="text-sm opacity-80">€{variant.price.toFixed(2)}</span>
                    {variant.is_default && (
                      <Badge variant="secondary" className="mt-1 text-xs">Standard</Badge>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* === "OHNE" SECTION (Always above Extras) === */}
          {product.has_removable_ingredients && removableIngredients.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Ban className="h-4 w-4 text-destructive" />
                <h4 className="font-medium text-sm">OHNE (tippen zum Abwählen)</h4>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {removableIngredients.map(ingredient => {
                  const isRemoved = removedIngredients.find(i => i.ingredient_id === ingredient.ingredient_id);
                  
                  return (
                    <Button
                      key={ingredient.ingredient_id}
                      variant={isRemoved ? "destructive" : "outline"}
                      size="sm"
                      className={`
                        transition-all duration-150
                        ${isRemoved ? 'line-through' : ''}
                        ${ingredient.show_prominent ? 'ring-2 ring-red-200' : ''}
                      `}
                      onClick={() => toggleIngredient(ingredient)}
                      data-testid={`ohne-${ingredient.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {isRemoved && <X className="h-3 w-3 mr-1" />}
                      {ingredient.name}
                    </Button>
                  );
                })}
              </div>
              
              {/* Show what's removed */}
              {removedIngredients.length > 0 && (
                <div className="mt-2 p-2 bg-destructive/10 rounded-md">
                  <div className="text-sm text-destructive font-medium">
                    {removedIngredients.map(ing => (
                      <div key={ing.ingredient_id} className="flex items-center gap-1">
                        <X className="h-3 w-3" />
                        OHNE {ing.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Separator if both OHNE and Extras exist */}
          {removableIngredients.length > 0 && allExtras.length > 0 && (
            <Separator />
          )}

          {/* === EXTRAS SECTION === */}
          {product.has_extras && allExtras.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-emerald-500" />
                <h4 className="font-medium text-sm">Extras hinzufügen</h4>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {allExtras.map(extra => {
                  const isSelected = selectedExtras.find(e => e.extra_id === extra.extra_id);
                  
                  return (
                    <Button
                      key={extra.extra_id}
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      className={`
                        transition-all duration-150
                        ${isSelected ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:border-emerald-300'}
                      `}
                      onClick={() => toggleExtra(extra)}
                      data-testid={`extra-${extra.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {isSelected && <Check className="h-3 w-3 mr-1" />}
                      {extra.name}
                      {extra.price > 0 && (
                        <span className="ml-1 opacity-70">+€{extra.price.toFixed(2)}</span>
                      )}
                    </Button>
                  );
                })}
              </div>
              
              {/* Show selected extras */}
              {selectedExtras.length > 0 && (
                <div className="mt-2 p-2 bg-emerald-500/10 rounded-md">
                  <div className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                    {selectedExtras.map(extra => (
                      <div key={extra.extra_id} className="flex items-center gap-1">
                        <Plus className="h-3 w-3" />
                        {extra.name}
                        {extra.price > 0 && <span className="opacity-70">+€{extra.price.toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary for complex orders */}
          {(removedIngredients.length > 0 || selectedExtras.length > 0) && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <h4 className="font-medium text-sm mb-1">Zusammenfassung</h4>
              <div className="text-sm text-muted-foreground">
                {selectedVariant && <div>• {selectedVariant.name}</div>}
                {removedIngredients.map(ing => (
                  <div key={ing.ingredient_id} className="text-destructive">• {ing.removal_label}</div>
                ))}
                {selectedExtras.map(extra => (
                  <div key={extra.extra_id} className="text-emerald-600">
                    • + {extra.name} {extra.price > 0 && `(+€${extra.price.toFixed(2)})`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirm}
            className="flex-1"
            data-testid="add-to-cart-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            Zum Warenkorb (€{totalPrice.toFixed(2)})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Quick "OHNE" Strip - Inline component for cart items
export function OhneStrip({ item, onUpdate }) {
  if (!item?.removedIngredients?.length) return null;
  
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {item.removedIngredients.map(ing => (
        <Badge 
          key={ing.ingredient_id}
          variant="destructive"
          className="text-xs"
        >
          ❌ {ing.removal_label || `OHNE ${ing.name}`}
        </Badge>
      ))}
    </div>
  );
}

// Quick "Extras" Strip - Inline component for cart items
export function ExtrasStrip({ item }) {
  if (!item?.selectedExtras?.length) return null;
  
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {item.selectedExtras.map(extra => (
        <Badge 
          key={extra.extra_id}
          variant="secondary"
          className="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100"
        >
          + {extra.name}
        </Badge>
      ))}
    </div>
  );
}
